/**
 * The palm lock form's finisher (lane C0-VFX-PORT; the user approved it 2026-10-11): a giant qi palm, in the golden
 * dragon palm's look (gold body, the master's dark outline, light from the upper right, a qi halo, a scaled dragon cuff
 * at the wrist). It gathers from motes above the foe, descends, presses the foe into the ground (rings, a crater of
 * cracks, gold motes), holds, then dissolves. Original design; the idea of a giant pressing palm is common wuxia stock,
 * nothing is traced.
 *
 * Coordinates as the ink samples (render/ink/samples.ts). The palm is one cached picture per fade step.
 */
import { clamp01, easeIn, easeOut, hash2, span } from './noise';
import { INK_PALETTES, type InkTone } from './palette';
import { foeX, P, type InkLayer, type InkScene } from './samples';
import { detailQuality, drawPic, pic, ringPic, type Pic } from './detail/draw';
import { rampOf } from './swords';

const GOLD = { lit: '#fff3b0', mid: '#f2c64a', shade: '#c8922a', deep: '#8a5a14', line: '#5a3408' } as const;

/** Signed distance to a capsule from (ax, ay) to (bx, by) of radius r (negative inside). */
function capsule(x: number, y: number, ax: number, ay: number, bx: number, by: number, r: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  const t = clamp01(((x - ax) * dx + (y - ay) * dy) / (dx * dx + dy * dy || 1));
  return Math.hypot(x - ax - dx * t, y - ay - dy * t) - r;
}

/**
 * The giant palm, fingers down, as a cached picture (origin: the middle of the fingertips' line, art px). `fade` 0–3
 * dithers it away. About 64 px wide and 96 tall at full size.
 */
function palmPic(kind: InkTone, fade: number): Pic {
  const acc = INK_PALETTES[kind];
  return pic(`giantPalm|${kind}|${fade}`, -40, -104, 40, 6, (set) => {
    // The shape: the palm (a rounded block), four fingers hanging down, the thumb out to the side, the wrist and cuff.
    const shape = (x: number, y: number): number =>
      Math.min(
        capsule(x, y, -12, -58, 12, -58, 16), // palm
        capsule(x, y, -16, -44, -18, -12, 3.6), // little finger
        capsule(x, y, -6, -46, -7, -4, 3.9), // ring finger
        capsule(x, y, 5, -46, 5, -2, 4), // middle finger
        capsule(x, y, 16, -44, 17, -8, 3.8), // index finger
        capsule(x, y, 24, -62, 33, -42, 5.2), // thumb
        capsule(x, y, 0, -78, 0, -98, 13), // wrist
      );
    for (let y = -104; y <= 6; y++)
      for (let x = -40; x <= 40; x++) {
        if (fade > 0 && ((x * 3 + y * 5) & 3) < fade) continue;
        const d = shape(x + 0.5, y + 0.5);
        if (d > 3) continue;
        // Qi halo round the hand, then the dark outline, then the body.
        if (d > 1.2) {
          set(x, y, d > 2.2 ? acc.acc : acc.accLit, 1);
          continue;
        }
        if (d > 0) {
          set(x, y, GOLD.line, 1);
          continue;
        }
        let col: string = GOLD.mid;
        // Light from the upper right: the right of each finger and the palm's top-right lit, the left shaded.
        const lit = shape(x - 1.5, y + 1.5) > 0;
        const dark = shape(x + 1.8, y - 1.2) > 0;
        if (lit) col = GOLD.lit;
        else if (dark) col = GOLD.shade;
        // The cuff: a band of dragon scales round the wrist (rows of small arcs).
        if (y < -80 && y > -96) {
          const row = Math.floor((y + 96) / 4);
          const sx = ((x + (row % 2) * 3) % 6 + 6) % 6;
          const sy = ((y + 96) % 4 + 4) % 4;
          col = sy === 3 || (sy === 2 && (sx === 0 || sx === 5)) ? GOLD.deep : lit ? GOLD.lit : GOLD.shade;
        }
        // Palm lines (qi light), the joints of the fingers.
        // Two curved creases across the palm (qi light), not straight lines.
        const c1 = Math.round(-62 + ((x + 4) * (x + 4)) / 60);
        const c2 = Math.round(-54 + ((x - 4) * (x - 4)) / 70);
        if ((y === c1 && x > -18 && x < 10) || (y === c2 && x > -8 && x < 18)) col = acc.accLit;
        if ((y === -26 || y === -14) && x > -20 && x < 22 && shape(x + 0.5, y + 0.5) < -1) col = GOLD.deep;
        set(x, y, col, 1);
      }
  });
}

/**
 * The finisher `t` s into its timeline, the press landing at `hit`: motes gather into the palm high above the foe
 * (hit − 0.45 … hit − 0.2), it hangs with a glint (the tell), drops and presses (hit), holds, dissolves (hit + 0.45 …).
 */
export function drawGiantPalm(g: CanvasRenderingContext2D, t: number, sc: InkScene, layer: InkLayer, kind: InkTone, hit: number): void {
  const s = t - hit;
  if (s < -0.45 || s > 1) return;
  const fx = foeX(sc);
  const ground = sc.tdy + 4;
  const R = rampOf(kind);
  if (layer === 'ground') {
    if (s >= 0 && s < 0.7) {
      // The press: two rings and a crater of short cracks round the foe's feet.
      const r = Math.round(12 + 54 * easeOut(s / 0.35));
      drawPic(g, sc, ringPic(R, r, Math.round(r * 0.42), Math.min(3, Math.floor(span(s, 0.35, 0.7) * 4)), 2), fx, sc.tdy);
      const r2 = Math.round(8 + 28 * easeOut(span(s, 0.05, 0.4)));
      drawPic(g, sc, ringPic(R, r2, Math.round(r2 * 0.42), Math.min(3, Math.floor(span(s, 0.4, 0.7) * 4)), 1), fx, sc.tdy);
      const p = INK_PALETTES[kind];
      for (let i = 0; i < 12; i++) {
        if (s > 0.55 && hash2(i, 5, 1400) < (s - 0.55) * 6) continue;
        const a = (i / 12) * Math.PI * 2 + hash2(i, 1, 1400) * 0.4;
        const len = 10 + 14 * hash2(i, 2, 1400);
        for (let k = 4; k < len; k++) {
          const [X, Y] = P(sc, fx + Math.cos(a) * k * 1.6, sc.tdy + Math.sin(a) * k * 0.7);
          g.fillStyle = k < 8 ? p.accLit : p.accDark;
          g.fillRect(Math.round(X), Math.round(Y), 1, 1);
        }
      }
    }
    return;
  }
  const p = INK_PALETTES[kind];
  if (s < -0.2) {
    // Gathering: gold motes drawn into the palm's place, the palm dithering in.
    const u = (s + 0.45) / 0.25;
    for (let i = 0; i < Math.round(20 * detailQuality()); i++) {
      const a = (i / 20) * Math.PI * 2 + u * 3;
      const r = 70 * (1 - easeIn(u)) * (0.6 + 0.5 * hash2(i, 1, 1410));
      const [X, Y] = P(sc, fx + Math.cos(a) * r, ground - 130 + Math.sin(a) * r * 0.7);
      g.fillStyle = i % 2 ? p.accLit : '#fff3b0';
      g.fillRect(Math.round(X), Math.round(Y), 2, 2);
    }
    if (u > 0.3) drawPic(g, sc, palmPic(kind, 3 - Math.min(3, Math.floor(((u - 0.3) / 0.7) * 4))), fx, ground - 80);
    return;
  }
  if (s < 0) {
    // Hang (a glint at the fingertips), then the drop with speed streaks above.
    const k = (s + 0.2) / 0.2;
    const drop = k < 0.4 ? 0 : easeIn((k - 0.4) / 0.6);
    const tip = ground - 80 + 80 * drop;
    if (drop > 0)
      for (let j = 0; j < 6; j++) {
        const x = fx - 24 + j * 10;
        for (let y = 0; y < 30; y += 2) {
          const [X, Y] = P(sc, x, tip - 104 - y);
          g.fillStyle = y < 10 ? p.accLit : p.acc;
          g.fillRect(Math.round(X), Math.round(Y), 1, 2);
        }
      }
    drawPic(g, sc, palmPic(kind, 0), fx, tip);
    if (drop === 0 && k > 0.1) {
      const [X, Y] = P(sc, fx, ground - 82);
      const r = 3 + Math.round(k * 10);
      g.fillStyle = '#ffffff';
      g.fillRect(Math.round(X) - r, Math.round(Y), r * 2 + 1, 1);
      g.fillRect(Math.round(X), Math.round(Y) - r, 1, r * 2 + 1);
    }
    return;
  }
  // Pressed into the ground: the palm sinks a little and shakes, holds, then dissolves into gold motes.
  const sink = Math.min(6, s * 40);
  const shake = s < 0.12 ? Math.round(Math.sin(s * 90) * 2) : 0;
  if (s < 0.5) drawPic(g, sc, palmPic(kind, s > 0.4 ? 1 : 0), fx + shake, ground + sink);
  else if (s < 0.62) drawPic(g, sc, palmPic(kind, 2 + Math.floor((s - 0.5) / 0.06)), fx, ground + sink);
  if (s < 0.06) {
    const [X, Y] = P(sc, fx, ground - 10);
    g.fillStyle = '#ffffff';
    g.fillRect(Math.round(X) - 40, Math.round(Y), 81, 2);
    g.fillRect(Math.round(X) - 2, Math.round(Y) - 20, 4, 30);
  }
  if (s > 0.45)
    for (let i = 0; i < Math.round(22 * detailQuality()); i++) {
      const u = clamp01((s - 0.45 - (i % 5) * 0.03) / 0.5);
      if (u <= 0 || u >= 1) continue;
      const [X, Y] = P(sc, fx + (hash2(i, 1, 1420) - 0.5) * 70, ground - 10 - 90 * hash2(i, 2, 1420) - u * 40);
      g.fillStyle = u < 0.5 ? '#fff3b0' : p.acc;
      g.fillRect(Math.round(X), Math.round(Y), 2, 2);
    }
}
